import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Banknote,
  Download,
  Loader2,
  Pencil,
  Plus,
  ReceiptText,
  Search,
  Settings2,
  Trash2,
  TrendingDown,
  TrendingUp,
  WalletCards,
} from "lucide-react";
import { http, apiError, rupiah } from "@/lib/api";
import AdminShell, { PageHead, useAdmin } from "@/components/AdminShell";
import { Field, Modal } from "@/pages/admin/Products";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal";

function todayInput() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function monthStartInput() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}-01`;
}

function nowLocalInput() {
  const d = new Date();
  const pad = (v) => String(v).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function cleanMoney(value) {
  return String(value || "").replace(/[^\d]/g, "").slice(0, 15);
}

function moneyInput(value) {
  const raw = cleanMoney(value);
  if (!raw) return "";
  return Number(raw).toLocaleString("id-ID");
}

function formatDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(d);
}

function FinanceInner() {
  const admin = useAdmin();
  const isOwner = admin.user?.role === "owner";

  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState({
    opening_balance: 0,
    auto_sales_enabled: true,
  });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [start, setStart] = useState(monthStartInput());
  const [end, setEnd] = useState(todayInput());
  const [typeFilter, setTypeFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [q, setQ] = useState("");

  const [transactionModal, setTransactionModal] = useState(null);
  const [transactionBusy, setTransactionBusy] = useState(false);
  const [transactionForm, setTransactionForm] = useState({
    type: "expense",
    category_id: "",
    amount: "",
    note: "",
    transaction_at: nowLocalInput(),
  });

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    opening_balance: "",
    auto_sales_enabled: true,
  });

  const [categoryOpen, setCategoryOpen] = useState(false);
  const [categoryBusy, setCategoryBusy] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: "",
    type: "expense",
  });

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const categoryMap = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.id, c])),
    [categories]
  );

  const filteredCategoryChoices = useMemo(
    () =>
      categories.filter(
        (c) =>
          c.active !== false &&
          (!transactionForm.type || c.type === transactionForm.type)
      ),
    [categories, transactionForm.type]
  );

  async function loadBase() {
    const [summaryRes, categoriesRes, settingsRes] = await Promise.all([
      http.get("/admin/finance/summary"),
      http.get("/admin/finance/categories"),
      http.get("/admin/finance/settings"),
    ]);

    setSummary(summaryRes.data);
    setCategories(categoriesRes.data || []);
    setSettings(settingsRes.data || {});
  }

  async function loadTransactions() {
    const params = new URLSearchParams();

    if (start) params.set("start", start);
    if (end) params.set("end", end);
    if (typeFilter) params.set("type", typeFilter);
    if (categoryFilter) params.set("category_id", categoryFilter);
    if (q.trim()) params.set("q", q.trim());
    params.set("limit", "1000");

    const { data } = await http.get(
      `/admin/finance/transactions?${params.toString()}`
    );
    setTransactions(data || []);
  }

  async function loadAll() {
    if (!isOwner) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      await Promise.all([loadBase(), loadTransactions()]);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
  }, [isOwner]);

  useEffect(() => {
    if (!isOwner) return;

    const timer = setTimeout(() => {
      loadTransactions().catch((e) => toast.error(apiError(e)));
    }, 180);

    return () => clearTimeout(timer);
  }, [start, end, typeFilter, categoryFilter, q, isOwner]);

  function openCreate(type) {
    const firstCategory = categories.find(
      (c) => c.active !== false && c.type === type
    );

    setTransactionModal({ mode: "create" });
    setTransactionForm({
      type,
      category_id: firstCategory?.id || "",
      amount: "",
      note: "",
      transaction_at: nowLocalInput(),
    });
  }

  function openEdit(row) {
    if (row.source_type === "order") return;

    const local = row.transaction_at
      ? new Date(row.transaction_at)
      : new Date();

    const pad = (v) => String(v).padStart(2, "0");
    const localValue = Number.isNaN(local.getTime())
      ? nowLocalInput()
      : `${local.getFullYear()}-${pad(local.getMonth() + 1)}-${pad(
          local.getDate()
        )}T${pad(local.getHours())}:${pad(local.getMinutes())}`;

    setTransactionModal({ mode: "edit", row });
    setTransactionForm({
      type: row.type,
      category_id: row.category_id || "",
      amount: String(row.amount || ""),
      note: row.note || "",
      transaction_at: localValue,
    });
  }

  async function saveTransaction() {
    const amount = Number(cleanMoney(transactionForm.amount));

    if (!["income", "expense"].includes(transactionForm.type)) {
      return toast.error("Jenis transaksi tidak valid");
    }

    if (!transactionForm.category_id) {
      return toast.error("Kategori wajib dipilih");
    }

    if (!Number.isInteger(amount) || amount <= 0) {
      return toast.error("Nominal harus lebih dari 0");
    }

    if (!transactionForm.transaction_at) {
      return toast.error("Tanggal dan jam wajib diisi");
    }

    setTransactionBusy(true);

    try {
      const body = {
        type: transactionForm.type,
        category_id: transactionForm.category_id,
        amount,
        note: transactionForm.note.trim(),
        transaction_at: transactionForm.transaction_at,
      };

      if (transactionModal?.mode === "edit") {
        await http.patch(
          `/admin/finance/transactions/${transactionModal.row.id}`,
          body
        );
        toast.success("Transaksi diperbarui");
      } else {
        await http.post("/admin/finance/transactions", body);
        toast.success("Transaksi ditambahkan");
      }

      setTransactionModal(null);
      await Promise.all([loadBase(), loadTransactions()]);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setTransactionBusy(false);
    }
  }

  function openSettings() {
    setSettingsForm({
      opening_balance: String(settings.opening_balance || 0),
      auto_sales_enabled: settings.auto_sales_enabled !== false,
    });
    setSettingsOpen(true);
  }

  async function saveSettings() {
    const openingBalance = Number(cleanMoney(settingsForm.opening_balance || 0));

    if (!Number.isInteger(openingBalance) || openingBalance < 0) {
      return toast.error("Saldo awal tidak valid");
    }

    setSettingsBusy(true);

    try {
      await http.patch("/admin/finance/settings", {
        opening_balance: openingBalance,
        auto_sales_enabled: Boolean(settingsForm.auto_sales_enabled),
      });

      toast.success("Pengaturan keuangan disimpan");
      setSettingsOpen(false);
      await Promise.all([loadBase(), loadTransactions()]);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSettingsBusy(false);
    }
  }

  async function addCategory() {
    const name = newCategory.name.trim();

    if (!name) {
      return toast.error("Nama kategori wajib diisi");
    }

    setCategoryBusy(true);

    try {
      await http.post("/admin/finance/categories", {
        name,
        type: newCategory.type,
      });

      toast.success("Kategori ditambahkan");
      setNewCategory({ name: "", type: "expense" });

      const { data } = await http.get("/admin/finance/categories");
      setCategories(data || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCategoryBusy(false);
    }
  }

  async function removeCategory(category) {
    setCategoryBusy(true);

    try {
      await http.delete(`/admin/finance/categories/${category.id}`);
      toast.success("Kategori dihapus");

      const { data } = await http.get("/admin/finance/categories");
      setCategories(data || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCategoryBusy(false);
    }
  }

  async function confirmDeleteTransaction() {
    if (!deleteTarget) return;

    setDeleteBusy(true);

    try {
      await http.delete(`/admin/finance/transactions/${deleteTarget.id}`);
      toast.success("Transaksi dihapus");
      setDeleteTarget(null);
      await Promise.all([loadBase(), loadTransactions()]);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDeleteBusy(false);
    }
  }

  async function downloadExcel() {
    setExporting(true);

    try {
      const params = new URLSearchParams();

      if (start) params.set("start", start);
      if (end) params.set("end", end);
      if (typeFilter) params.set("type", typeFilter);
      if (categoryFilter) params.set("category_id", categoryFilter);
      if (q.trim()) params.set("q", q.trim());

      const response = await http.get(
        `/admin/finance/export?${params.toString()}`,
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `keuangan-${start || "all"}-sd-${end || "all"}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      toast.success("Excel keuangan berhasil diunduh");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setExporting(false);
    }
  }

  if (!isOwner) {
    return (
      <div className="rounded-3xl border border-dashed border-border p-12 text-center text-muted-foreground">
        Menu Keuangan hanya dapat diakses pemilik.
      </div>
    );
  }

  if (loading || !summary) {
    return (
      <div className="flex justify-center pt-20">
        <Loader2 className="wm-spin text-muted-foreground" />
      </div>
    );
  }

  const cards = [
    {
      label: "Saldo Usaha",
      value: summary.balance,
      Icon: WalletCards,
      tone: "text-primary bg-primary/15",
    },
    {
      label: "Pemasukan Bulan Ini",
      value: summary.month_income,
      Icon: TrendingUp,
      tone: "text-emerald-500 bg-emerald-500/15",
    },
    {
      label: "Pengeluaran Bulan Ini",
      value: summary.month_expense,
      Icon: TrendingDown,
      tone: "text-rose-500 bg-rose-500/15",
    },
    {
      label: "Arus Bersih Bulan Ini",
      value: summary.month_net,
      Icon: Banknote,
      tone:
        summary.month_net >= 0
          ? "text-emerald-500 bg-emerald-500/15"
          : "text-rose-500 bg-rose-500/15",
    },
  ];

  return (
    <>
      <PageHead
        eyebrow="Owner"
        title="Keuangan"
        desc="Saldo usaha, pemasukan, pengeluaran, kategori, dan riwayat transaksi."
      >
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={openSettings}
            className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-bold"
          >
            <Settings2 size={16} />
            Pengaturan
          </button>

          <button
            type="button"
            onClick={() => openCreate("income")}
            className="flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2.5 text-sm font-bold text-white"
          >
            <ArrowUpCircle size={16} />
            Pemasukan
          </button>

          <button
            type="button"
            onClick={() => openCreate("expense")}
            className="flex items-center gap-2 rounded-xl bg-rose-500 px-4 py-2.5 text-sm font-bold text-white"
          >
            <ArrowDownCircle size={16} />
            Pengeluaran
          </button>
        </div>
      </PageHead>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map(({ label, value, Icon, tone }) => (
          <div
            key={label}
            className="rounded-3xl border border-border bg-card p-5"
          >
            <div className={`grid h-10 w-10 place-items-center rounded-xl ${tone}`}>
              <Icon size={18} />
            </div>
            <div className="mt-4 text-xs font-semibold text-muted-foreground">
              {label}
            </div>
            <div className="mt-1 font-heading text-2xl font-extrabold tracking-tight text-card-foreground">
              {rupiah(value)}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-3xl border border-border bg-card p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.12em] text-muted-foreground">
              Saldo Awal
            </div>
            <div className="mt-1 text-sm font-bold">{rupiah(summary.opening_balance)}</div>
          </div>

          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.12em] text-muted-foreground">
              Penjualan Lunas
            </div>
            <div className="mt-1 text-sm font-bold">
              {summary.auto_sales_enabled ? rupiah(summary.sales_total) : "Nonaktif"}
            </div>
          </div>

          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.12em] text-muted-foreground">
              Pemasukan Manual
            </div>
            <div className="mt-1 text-sm font-bold text-emerald-500">
              + {rupiah(summary.manual_income_total)}
            </div>
          </div>

          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.12em] text-muted-foreground">
              Pengeluaran Manual
            </div>
            <div className="mt-1 text-sm font-bold text-rose-500">
              - {rupiah(summary.manual_expense_total)}
            </div>
          </div>
        </div>

        <div className="mt-3 rounded-2xl bg-muted/50 p-3 text-xs leading-5 text-muted-foreground">
          Rumus saldo: <b className="text-card-foreground">Saldo Awal + Penjualan Lunas + Pemasukan Manual − Pengeluaran Manual</b>.
          Perhitungan dilakukan di backend dengan nilai Rupiah bilangan bulat, bukan dari angka tampilan browser.
        </div>
      </div>

      <div className="mt-4 rounded-3xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-heading text-lg font-extrabold">Riwayat Keuangan</h2>
            <div className="text-xs text-muted-foreground">
              Penjualan lunas otomatis ditandai sebagai transaksi sistem dan tidak dapat diedit.
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setCategoryOpen(true)}
              className="flex items-center gap-2 rounded-xl bg-muted px-3 py-2.5 text-xs font-bold"
            >
              <Plus size={14} />
              Kategori
            </button>

            <button
              type="button"
              disabled={exporting}
              onClick={downloadExcel}
              className="flex items-center gap-2 rounded-xl bg-primary px-3 py-2.5 text-xs font-bold text-primary-foreground disabled:opacity-50"
            >
              {exporting ? <Loader2 size={14} className="wm-spin" /> : <Download size={14} />}
              Unduh Excel
            </button>
          </div>
        </div>

        <div className="mt-4 grid gap-2 md:grid-cols-5">
          <input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="input"
            title="Dari tanggal"
          />

          <input
            type="date"
            value={end}
            min={start || undefined}
            onChange={(e) => setEnd(e.target.value)}
            className="input"
            title="Sampai tanggal"
          />

          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setCategoryFilter("");
            }}
            className="input"
          >
            <option value="">Semua Jenis</option>
            <option value="income">Pemasukan</option>
            <option value="expense">Pengeluaran</option>
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="input"
          >
            <option value="">Semua Kategori</option>
            {categories
              .filter((c) => !typeFilter || c.type === typeFilter)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>

          <div className="flex items-center rounded-xl border border-border bg-muted px-3">
            <Search size={14} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari catatan / order"
              className="w-full bg-transparent px-2 py-2.5 text-sm outline-none"
            />
          </div>
        </div>

        <div className="mt-4 space-y-2">
          {transactions.map((row) => {
            const income = row.type === "income";
            const automatic = row.source_type === "order";

            return (
              <div
                key={`${row.source_type}-${row.id}`}
                className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-muted/35 p-4"
              >
                <div className="flex min-w-0 items-start gap-3">
                  <div
                    className={`grid h-10 w-10 flex-none place-items-center rounded-xl ${
                      income
                        ? "bg-emerald-500/15 text-emerald-500"
                        : "bg-rose-500/15 text-rose-500"
                    }`}
                  >
                    {automatic ? (
                      <ReceiptText size={17} />
                    ) : income ? (
                      <ArrowUpCircle size={17} />
                    ) : (
                      <ArrowDownCircle size={17} />
                    )}
                  </div>

                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <b className="text-sm text-card-foreground">
                        {row.category_name || categoryMap[row.category_id]?.name || "-"}
                      </b>

                      {automatic && (
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[9px] font-black uppercase text-primary">
                          Otomatis
                        </span>
                      )}
                    </div>

                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {formatDateTime(row.transaction_at)}
                    </div>

                    {row.note && (
                      <div className="mt-1 max-w-2xl break-words text-xs text-muted-foreground">
                        {row.note}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div
                    className={`text-right font-heading text-base font-extrabold ${
                      income ? "text-emerald-500" : "text-rose-500"
                    }`}
                  >
                    {income ? "+" : "-"} {rupiah(row.amount)}
                  </div>

                  {!automatic && (
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => openEdit(row)}
                        className="grid h-8 w-8 place-items-center rounded-lg bg-card"
                        title="Edit transaksi"
                      >
                        <Pencil size={13} />
                      </button>

                      <button
                        type="button"
                        onClick={() => setDeleteTarget(row)}
                        className="grid h-8 w-8 place-items-center rounded-lg bg-destructive/10 text-destructive"
                        title="Hapus transaksi"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {transactions.length === 0 && (
            <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
              Belum ada transaksi pada filter ini.
            </div>
          )}
        </div>
      </div>

      {transactionModal && (
        <Modal
          onClose={() => !transactionBusy && setTransactionModal(null)}
          title={
            transactionModal.mode === "edit"
              ? "Edit Transaksi"
              : transactionForm.type === "income"
              ? "Tambah Pemasukan"
              : "Tambah Pengeluaran"
          }
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => {
                  const first = categories.find(
                    (c) => c.active !== false && c.type === "income"
                  );
                  setTransactionForm((p) => ({
                    ...p,
                    type: "income",
                    category_id: first?.id || "",
                  }));
                }}
                className={`rounded-xl border px-3 py-3 text-sm font-bold ${
                  transactionForm.type === "income"
                    ? "border-emerald-500 bg-emerald-500/10 text-emerald-500"
                    : "border-border"
                }`}
              >
                Pemasukan
              </button>

              <button
                type="button"
                onClick={() => {
                  const first = categories.find(
                    (c) => c.active !== false && c.type === "expense"
                  );
                  setTransactionForm((p) => ({
                    ...p,
                    type: "expense",
                    category_id: first?.id || "",
                  }));
                }}
                className={`rounded-xl border px-3 py-3 text-sm font-bold ${
                  transactionForm.type === "expense"
                    ? "border-rose-500 bg-rose-500/10 text-rose-500"
                    : "border-border"
                }`}
              >
                Pengeluaran
              </button>
            </div>

            <Field label="Kategori">
              <select
                value={transactionForm.category_id}
                onChange={(e) =>
                  setTransactionForm((p) => ({
                    ...p,
                    category_id: e.target.value,
                  }))
                }
                className="input"
              >
                <option value="">Pilih kategori</option>
                {filteredCategoryChoices.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Nominal">
              <div className="flex overflow-hidden rounded-xl border border-border bg-muted">
                <div className="flex min-w-[54px] items-center justify-center border-r border-border px-3 text-sm font-extrabold text-muted-foreground">
                  Rp
                </div>
                <input
                  inputMode="numeric"
                  value={moneyInput(transactionForm.amount)}
                  onChange={(e) =>
                    setTransactionForm((p) => ({
                      ...p,
                      amount: cleanMoney(e.target.value),
                    }))
                  }
                  placeholder="0"
                  className="min-w-0 flex-1 bg-transparent px-3 py-3 text-lg font-extrabold text-card-foreground outline-none"
                />
              </div>
            </Field>

            <Field label="Tanggal & Jam">
              <input
                type="datetime-local"
                step="60"
                value={transactionForm.transaction_at}
                onChange={(e) =>
                  setTransactionForm((p) => ({
                    ...p,
                    transaction_at: e.target.value,
                  }))
                }
                className="input"
              />
            </Field>

            <Field label="Catatan">
              <textarea
                rows={3}
                maxLength={500}
                value={transactionForm.note}
                onChange={(e) =>
                  setTransactionForm((p) => ({
                    ...p,
                    note: e.target.value,
                  }))
                }
                placeholder="Contoh: Gaji September, belanja bahan dapur, bayar listrik..."
                className="input resize-none"
              />
            </Field>

            <button
              type="button"
              disabled={transactionBusy}
              onClick={saveTransaction}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 font-bold text-primary-foreground disabled:opacity-50"
            >
              {transactionBusy && <Loader2 size={16} className="wm-spin" />}
              Simpan Transaksi
            </button>
          </div>
        </Modal>
      )}

      {settingsOpen && (
        <Modal
          onClose={() => !settingsBusy && setSettingsOpen(false)}
          title="Pengaturan Keuangan"
        >
          <div className="space-y-4">
            <Field label="Saldo Awal">
              <div className="flex overflow-hidden rounded-xl border border-border bg-muted">
                <div className="flex min-w-[54px] items-center justify-center border-r border-border px-3 text-sm font-extrabold text-muted-foreground">
                  Rp
                </div>
                <input
                  inputMode="numeric"
                  value={moneyInput(settingsForm.opening_balance)}
                  onChange={(e) =>
                    setSettingsForm((p) => ({
                      ...p,
                      opening_balance: cleanMoney(e.target.value),
                    }))
                  }
                  placeholder="0"
                  className="min-w-0 flex-1 bg-transparent px-3 py-3 text-lg font-extrabold text-card-foreground outline-none"
                />
              </div>
            </Field>

            <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-border p-4">
              <input
                type="checkbox"
                checked={settingsForm.auto_sales_enabled}
                onChange={(e) =>
                  setSettingsForm((p) => ({
                    ...p,
                    auto_sales_enabled: e.target.checked,
                  }))
                }
                className="mt-1 h-4 w-4"
              />
              <span>
                <b className="text-sm text-card-foreground">
                  Penjualan lunas otomatis masuk saldo
                </b>
                <span className="mt-1 block text-xs leading-5 text-muted-foreground">
                  Hanya order dengan status pembayaran LUNAS yang dihitung.
                  Sistem tidak membuat duplikat transaksi karena penjualan dihitung langsung dari order.
                </span>
              </span>
            </label>

            <button
              type="button"
              disabled={settingsBusy}
              onClick={saveSettings}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 font-bold text-primary-foreground disabled:opacity-50"
            >
              {settingsBusy && <Loader2 size={16} className="wm-spin" />}
              Simpan Pengaturan
            </button>
          </div>
        </Modal>
      )}

      {categoryOpen && (
        <Modal onClose={() => !categoryBusy && setCategoryOpen(false)} title="Kategori Keuangan">
          <div className="space-y-4">
            <div className="grid gap-2 sm:grid-cols-[1fr_140px_auto]">
              <input
                value={newCategory.name}
                onChange={(e) =>
                  setNewCategory((p) => ({ ...p, name: e.target.value }))
                }
                placeholder="Nama kategori"
                className="input"
              />

              <select
                value={newCategory.type}
                onChange={(e) =>
                  setNewCategory((p) => ({ ...p, type: e.target.value }))
                }
                className="input"
              >
                <option value="expense">Pengeluaran</option>
                <option value="income">Pemasukan</option>
              </select>

              <button
                type="button"
                onClick={addCategory}
                disabled={categoryBusy}
                className="rounded-xl bg-primary px-4 py-2 text-sm font-bold text-primary-foreground disabled:opacity-50"
              >
                Tambah
              </button>
            </div>

            <div className="max-h-[50vh] space-y-2 overflow-y-auto">
              {categories.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-border p-3"
                >
                  <div>
                    <div className="text-sm font-bold">{c.name}</div>
                    <div
                      className={`text-[10px] font-black uppercase ${
                        c.type === "income" ? "text-emerald-500" : "text-rose-500"
                      }`}
                    >
                      {c.type === "income" ? "Pemasukan" : "Pengeluaran"}
                    </div>
                  </div>

                  {!c.system && (
                    <button
                      type="button"
                      disabled={categoryBusy}
                      onClick={() => removeCategory(c)}
                      className="grid h-8 w-8 place-items-center rounded-lg bg-destructive/10 text-destructive"
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </Modal>
      )}

      <ConfirmDeleteModal
        open={Boolean(deleteTarget)}
        title="Hapus Transaksi?"
        message="Transaksi manual ini akan dihapus dan saldo akan dihitung ulang otomatis."
        itemName={
          deleteTarget
            ? `${deleteTarget.category_name || "Transaksi"} • ${rupiah(deleteTarget.amount)}`
            : ""
        }
        busy={deleteBusy}
        onCancel={() => !deleteBusy && setDeleteTarget(null)}
        onConfirm={confirmDeleteTransaction}
      />

      <style>{`
        .input{
          width:100%;
          border:1px solid hsl(var(--border));
          background:hsl(var(--muted));
          border-radius:.75rem;
          padding:.65rem .8rem;
          font-size:.85rem;
          outline:none;
          color:hsl(var(--card-foreground));
        }
      `}</style>
    </>
  );
}

export default function Finance() {
  return (
    <AdminShell>
      <FinanceInner />
    </AdminShell>
  );
}
